// Suppliers data layer (Phase 4).
//
// The SINGLE interface between the app and the suppliers schema from
// supabase/migrations/20260630013156_suppliers.sql.
//
// SECURITY MODEL (enforced server-side; mirrored here):
//   • We NEVER send company_id or supplier_number — DB triggers own them
//     (company forced from auth.uid(); supplier_number auto = SUP-00001; both
//     immutable). RLS rejects anything out of company scope.
//   • All supplier DB access lives in THIS file (no Supabase in components).
//
// RETURN-TYPE CONTRACT (consistent with products.js):
//   • Collection reads → { data, count, page?, pageSize? }
//   • Single read      → T | null
//   • create / update  → the affected T
//   • delete           → void  (throws only on error)
//   • All functions THROW a mapped Error on failure.

import { supabase } from "./supabaseClient";

/**
 * @typedef {Object} Supplier
 * @property {string} id
 * @property {string} supplier_number  Auto, immutable (e.g. "SUP-00001").
 * @property {string} company_name
 * @property {"active"|"inactive"} status
 * @property {string|null} [trading_name]
 * @property {string|null} [contact_name]
 * @property {string|null} [email]
 * @property {string|null} [phone]
 * @property {string|null} [mobile]
 * @property {string|null} [website]
 * @property {string|null} [vat_number]
 * @property {string|null} [company_reg_number]
 * @property {string|null} [address_line1]
 * @property {string|null} [address_line2]
 * @property {string|null} [city]
 * @property {string|null} [county]
 * @property {string|null} [country]
 * @property {string|null} [postal_code]
 * @property {string|null} [payment_terms]
 * @property {string} [preferred_currency]
 * @property {number|null} [lead_time_days]
 * @property {number|null} [credit_limit]
 * @property {string|null} [notes]
 */

/** @typedef {{ data: T[], count: number, page?: number, pageSize?: number }} Page @template T */

const TABLE = "suppliers";

/** Default rows per page for paginated reads. */
export const DEFAULT_PAGE_SIZE = 50;

/** Selectable status values (UI dropdowns + parity with the DB CHECK). */
export const SUPPLIER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

/** Payment terms (shared vocabulary with customers). */
export const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_7", label: "Net 7 days" },
  { value: "net_14", label: "Net 14 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
];

/** Currencies (UI dropdowns). */
export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];

/** Sort keys accepted by getSuppliers({ sortBy }). */
export const SUPPLIER_SORTS = [
  { value: "number", label: "Supplier No." },
  { value: "name", label: "Company" },
  { value: "status", label: "Status" },
  { value: "created", label: "Newest" },
];

// Columns the client may write. company_id / supplier_number / created_by / id /
// timestamps are deliberately absent — the DB owns them.
const WRITE_FIELDS = [
  "company_name", "trading_name", "contact_name", "email", "phone", "mobile",
  "website", "vat_number", "company_reg_number", "address_line1", "address_line2",
  "city", "county", "country", "postal_code", "payment_terms", "preferred_currency",
  "lead_time_days", "credit_limit", "status", "notes",
];

const SORT_COLUMNS = { number: "supplier_number", name: "company_name", status: "status", created: "created_at" };

const SELECT = "*";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};

const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the status field."; break;
    case "23502": message = "A required field is missing."; break;
    case "23505": message = "A supplier with these details already exists."; break;
    case "PGRST116": message = "Supplier not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message);
  e.code = code;
  e.cause = error;
  return e;
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List suppliers for the caller's company (RLS-scoped), with search, status
 * filter, sorting and pagination.
 *
 * @param {Object} [opts]
 * @param {string} [opts.search]   Free text matched (ILIKE) against supplier_number, company_name, trading_name, contact_name, email, phone, vat_number.
 * @param {"all"|"active"|"inactive"} [opts.status]  Status filter ("all"/undefined = no filter).
 * @param {number} [opts.page=1]
 * @param {number} [opts.pageSize=50]
 * @param {"number"|"name"|"status"|"created"} [opts.sortBy="name"]
 * @param {"asc"|"desc"} [opts.sortDir="asc"]
 * @returns {Promise<Page<Supplier>>}
 * @throws {Error}
 */
export async function getSuppliers(opts = {}) {
  const { search, status, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "name", sortDir = "asc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "company_name";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  let q = c.from(TABLE).select(SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, to);
  if (status && status !== "all") q = q.eq("status", status);
  const term = sanitizeTerm(search);
  if (term) {
    q = q.or(`supplier_number.ilike.%${term}%,company_name.ilike.%${term}%,trading_name.ilike.%${term}%,contact_name.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%,vat_number.ilike.%${term}%`);
  }

  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load suppliers");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

/**
 * Convenience wrapper around getSuppliers for a search term.
 * @param {string} term
 * @param {Object} [opts]
 * @returns {Promise<Page<Supplier>>}
 * @throws {Error}
 */
export async function searchSuppliers(term, opts = {}) {
  return getSuppliers({ ...opts, search: term });
}

/**
 * Fetch a single supplier by id (RLS-scoped).
 * @param {string} id
 * @returns {Promise<Supplier|null>}
 * @throws {Error}
 */
export async function getSupplier(id) {
  const { data, error } = await client().from(TABLE).select(SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load supplier");
  return data || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Create a supplier. company_id and supplier_number are assigned server-side.
 * @param {Partial<Supplier>} payload
 * @returns {Promise<Supplier>}
 * @throws {Error}
 */
export async function createSupplier(payload) {
  const { data, error } = await client().from(TABLE).insert(pick(payload, WRITE_FIELDS)).select(SELECT).single();
  if (error) throw toError(error, "Could not create supplier");
  return data;
}

/**
 * Update a supplier by id. company_id / supplier_number are immutable (DB-enforced) and never sent.
 * @param {string} id
 * @param {Partial<Supplier>} payload
 * @returns {Promise<Supplier>}
 * @throws {Error}
 */
export async function updateSupplier(id, payload) {
  const { data, error } = await client().from(TABLE).update(pick(payload, WRITE_FIELDS)).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update supplier");
  return data;
}

/**
 * Delete a supplier by id. No-op (no error) if not visible to the caller.
 * @param {string} id
 * @returns {Promise<void>}
 * @throws {Error}
 */
export async function deleteSupplier(id) {
  const { error } = await client().from(TABLE).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete supplier");
}
